import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../api';

const MONTHS_FULL = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сент', 'окт', 'нояб', 'дек'];
const QUARTER_ROMAN = ['I', 'II', 'III', 'IV'];

export const PRESETS = [
  { key: 'today', label: 'Сегодня' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
  { key: 'quarter', label: 'Квартал' },
  { key: 'year', label: 'Год' },
  { key: 'all', label: 'За всё время' },
  { key: 'custom', label: 'Произвольно' },
];

export const money = (n) => `${Math.round(Number(n || 0)).toLocaleString('ru')} ₽`;
export const pct = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('ru-RU') : '—');
export const formatHours = (h) => {
  if (h == null) return '—';
  if (h >= 24) return `${Math.floor(h / 24)} дн ${Math.round(h % 24)} ч`;
  if (h >= 1) return `${h.toFixed(1)} ч`;
  return `${Math.round(h * 60)} мин`;
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseYmd = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Периоды календарные (не «последние 7/30 дней»): неделя пн–вс, месяц с 1-го числа и т.д.
function presetRange(key, now = new Date()) {
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  switch (key) {
    case 'today': return { from: ymd(now), to: ymd(now) };
    case 'week': {
      const dow = (now.getDay() + 6) % 7; // пн = 0
      return { from: ymd(new Date(y, m, d - dow)), to: ymd(new Date(y, m, d - dow + 6)) };
    }
    case 'month': return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
    case 'quarter': {
      const q = Math.floor(m / 3);
      return { from: ymd(new Date(y, q * 3, 1)), to: ymd(new Date(y, q * 3 + 3, 0)) };
    }
    case 'year': return { from: ymd(new Date(y, 0, 1)), to: ymd(new Date(y, 11, 31)) };
    // «За всё время» — заведомо широкие границы: данных раньше 2000 и позже 2100 года нет.
    case 'all': return { from: '2000-01-01', to: '2100-12-31' };
    default: return null;
  }
}

// «28 сент — 4 окт 2026», «1 — 7 сент 2026», «29 дек 2025 — 4 янв 2026»
function rangeLabel(from, to) {
  const a = parseYmd(from), b = parseYmd(to);
  if (from === to) return `${a.getDate()} ${MONTHS_GEN[a.getMonth()]} ${a.getFullYear()}`;
  if (a.getFullYear() !== b.getFullYear()) {
    return `${a.getDate()} ${MONTHS_SHORT[a.getMonth()]} ${a.getFullYear()} — ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`;
  }
  if (a.getMonth() === b.getMonth()) return `${a.getDate()} — ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`;
  return `${a.getDate()} ${MONTHS_SHORT[a.getMonth()]} — ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`;
}

function periodLabel(preset, range) {
  if (!range) return '';
  const a = parseYmd(range.from);
  switch (preset) {
    case 'month': return `${MONTHS_FULL[a.getMonth()]} ${a.getFullYear()}`;
    case 'quarter': {
      const q = Math.floor(a.getMonth() / 3);
      return `${QUARTER_ROMAN[q]} квартал ${a.getFullYear()} (${MONTHS_SHORT[q * 3]} — ${MONTHS_SHORT[q * 3 + 2]})`;
    }
    case 'year': return `${a.getFullYear()} год`;
    case 'all': return 'За всё время';
    default: return rangeLabel(range.from, range.to);
  }
}

// Период хранится в адресе страницы (?p=month или ?p=custom&from=…&to=…): при переходе с
// дашборда в модуль и обратно (и при обновлении страницы) выбранный период не сбрасывается.
export function usePeriod() {
  const [params, setParams] = useSearchParams();
  const preset = PRESETS.some(p => p.key === params.get('p')) ? params.get('p') : 'month';
  const from = params.get('from') || '';
  const to = params.get('to') || '';

  const range = useMemo(() => {
    if (preset !== 'custom') return presetRange(preset);
    if (!DATE_RE.test(from) || !DATE_RE.test(to) || from > to) return null;
    return { from, to };
  }, [preset, from, to]);

  const update = useCallback((changes) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v == null || v === '' ? next.delete(k) : next.set(k, v)));
      return next;
    }, { replace: true });
  }, [setParams]);

  const setPreset = (key) => {
    if (key === 'custom') {
      const base = (preset !== 'all' && range) || presetRange('month');
      update({ p: 'custom', from: base.from, to: base.to });
    } else {
      update({ p: key, from: null, to: null });
    }
  };

  // Строка периода для ссылок в другие модули — чтобы переход сохранял выбранный период.
  const periodQuery = preset === 'custom' ? `p=custom&from=${from}&to=${to}` : `p=${preset}`;

  return { preset, range, from, to, setPreset, setCustom: (f, t) => update({ from: f, to: t }), periodQuery, label: periodLabel(preset, range) };
}

export function PeriodBar({ period }) {
  return (
    <div className="toolbar" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
      {PRESETS.map(p => (
        <button key={p.key} className={period.preset === p.key ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'} onClick={() => period.setPreset(p.key)}>
          {p.label}
        </button>
      ))}
      {period.preset === 'custom' && (
        <>
          <span className="text-muted">с</span>
          <input type="date" value={period.from} onChange={e => period.setCustom(e.target.value, period.to)} />
          <span className="text-muted">по</span>
          <input type="date" value={period.to} onChange={e => period.setCustom(period.from, e.target.value)} />
        </>
      )}
      <b style={{ marginLeft: 8 }}>{period.range ? period.label : 'Укажите корректный период'}</b>
    </div>
  );
}

// Переход в модуль сразу на нужный список (с сохранением периода).
export function useGo(period) {
  const navigate = useNavigate();
  return useCallback((module, list, extra = {}) => {
    const q = new URLSearchParams(period.periodQuery);
    if (list) q.set('list', list);
    Object.entries(extra).forEach(([k, v]) => v != null && q.set(k, v));
    navigate(`/finance/dashboard/${module}?${q.toString()}`);
  }, [navigate, period.periodQuery]);
}

// Каждый блок грузит свои данные сам: ошибка одного эндпоинта не роняет страницу целиком.
export function useBlock(path, params, reloadKey = 0) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const key = JSON.stringify(params || {});
  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: null }));
    api.get(path, { params })
      .then(r => { if (!cancelled) setState({ loading: false, error: null, data: r.data }); })
      .catch(e => { if (!cancelled) setState({ loading: false, error: e.response?.data?.error || 'Не удалось загрузить данные', data: null }); });
    return () => { cancelled = true; };
  }, [path, key, reloadKey]); // eslint-disable-line
  return state;
}

export function BlockState({ state, children }) {
  if (state.loading) return <div className="loading-text">Загрузка...</div>;
  if (state.error) return <div className="text-muted" style={{ padding: 20, textAlign: 'center' }}>⚠ {state.error}</div>;
  return children(state.data);
}

// Плитка; с onClick — кликабельная (открывает расшифровку цифры).
export function Tile({ value, label, hint, danger, onClick, active }) {
  return (
    <div
      className="stat-card"
      onClick={onClick}
      title={onClick ? 'Открыть расшифровку' : undefined}
      style={{
        flex: 1,
        cursor: onClick ? 'pointer' : undefined,
        borderColor: active ? 'var(--primary)' : danger ? 'var(--danger)' : undefined,
        boxShadow: active ? '0 0 0 1px var(--primary)' : undefined,
      }}
    >
      <div className="stat-value" style={danger ? { color: 'var(--danger)' } : undefined}>{value}</div>
      <div className="stat-label">{label}{onClick && ' →'}</div>
      {hint && <div className="stat-label">{hint}</div>}
    </div>
  );
}

// Строка «подпись — значение» внутри карточки; с onClick — кликабельная.
export function Row({ label, children, onClick, last, active }) {
  return (
    <div
      onClick={onClick}
      title={onClick ? 'Открыть расшифровку' : undefined}
      style={{
        display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 4px',
        borderBottom: last ? 'none' : '1px solid var(--border)',
        cursor: onClick ? 'pointer' : undefined,
        background: active ? 'var(--bg)' : undefined,
      }}
    >
      <span>{label}{onClick && <span className="text-muted"> →</span>}</span>
      {children}
    </div>
  );
}

export function SectionTitle({ children, extra, onClick }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <h3 style={{ margin: 0, cursor: onClick ? 'pointer' : undefined }} onClick={onClick} title={onClick ? 'Открыть модуль' : undefined}>
        {children}{onClick && <span className="text-muted" style={{ fontSize: 14 }}> →</span>}
      </h3>
      {extra}
    </div>
  );
}
